import { X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer'
import { Cap } from '../../primitives'
import type { GraphEdge, GraphNode } from './layout'
import { MonoRef } from './MonoRef'
import { StepBlock } from './StepBlock'
import { SIDE_STEP } from './tree'

interface StepDrawerProps {
  node: GraphNode | undefined
  incoming: GraphEdge[]
  outgoing: GraphEdge[]
  onSelect: (service: string) => void
  onClose: () => void
}

/** One step's wording and branches. Non-modal, so the diagram stays clickable. */
export function StepDrawer({ node, incoming, outgoing, onSelect, onClose }: StepDrawerProps) {
  return (
    <Drawer
      direction="right"
      open={Boolean(node)}
      onOpenChange={(open) => !open && onClose()}
      // Undismissable so clicking another step retargets the panel instead of closing it.
      modal={false}
      dismissible={false}
    >
      <DrawerContent
        hideOverlay
        className="border-ink-200 w-[92vw]! border-l sm:max-w-md!"
        onEscapeKeyDown={onClose}
      >
        <DrawerHeader className="border-ink-100 flex-row items-center justify-between border-b p-4">
          <DrawerTitle className="text-ink font-mono text-xs tracking-[0.08em] uppercase">
            {node?.stage
              ? `${String(node.stagePosition).padStart(2, '0')} — ${node.stage.name}`
              : 'End of flow'}
          </DrawerTitle>
          <Button
            variant="iconOnly"
            size="icon-sm"
            onClick={onClose}
            aria-label="Close"
            className="text-ink-400 hover:text-ink -mr-1"
          >
            <X className="size-4" />
          </Button>
        </DrawerHeader>

        <div className="flex flex-col gap-8 overflow-y-auto p-4">
          {node?.stage && <MonoRef value={node.stage.action} />}

          {node?.step ? (
            <StepBlock step={node.step} />
          ) : (
            <div className="text-ink-500 text-sm">
              Nothing further is asked in chat — the customer is sent to the dashboard.
            </div>
          )}

          <EdgeList
            label="Arrives from"
            edges={incoming}
            pick="from"
            empty="Entry point"
            onSelect={onSelect}
          />
          <EdgeList label="Leads to" edges={outgoing} pick="to" empty="Terminal" onSelect={onSelect} />

          <div>
            <Cap>Off-tree — from every step</Cap>
            <div className="mt-3">
              <StepBlock step={SIDE_STEP} />
            </div>
          </div>

          <div>
            <Cap>Source</Cap>
            <div className="mt-3 flex flex-col items-start gap-1">
              <MonoRef value="backend/insure/services/insurance/actions/" />
              <MonoRef value="backend/insure/services/insurance/constants.py" />
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
}

interface EdgeListProps {
  label: string
  edges: GraphEdge[]
  /** Which end of the edge is the other step. */
  pick: 'from' | 'to'
  empty: string
  onSelect: (service: string) => void
}

function EdgeList({ label, edges, pick, empty, onSelect }: EdgeListProps) {
  return (
    <div>
      <Cap>{label}</Cap>
      <ul className="mt-3 flex flex-col gap-2">
        {edges.length === 0 && <li className="text-ink-400 text-xs italic">{empty}</li>}
        {edges.map((edge) => (
          <li key={`${label}-${edge.from}-${edge.to}-${edge.label}`}>
            <Button
              variant="link"
              size="xs"
              onClick={() => onSelect(edge[pick])}
              className="h-auto justify-start px-0 font-mono text-[10px] font-normal underline underline-offset-2"
            >
              {edge[pick]}
            </Button>
            <div className="text-ink-400 text-xs">{edge.label}</div>
          </li>
        ))}
      </ul>
    </div>
  )
}
